import { StencilCDNImage } from '@/components/Common/StencilImage'
import SpinnerLoader from '@/components/Common/header/spinner-loader'
import { vyaparApiBaseUrl } from '@/root/api/apiConstants'
import { formatNumberToINRCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'
import {
  fetchPOSTRequest,
  fetchRequest,
} from '@/root/utils/network-adapter/fetch-request'
import Checkbox from '@/stories/components/Common/FormComponents/Checkbox'
import Modal from '@/stories/components/Common/Modal'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useMutation, useQuery } from 'react-query'

/* eslint-disable max-lines-per-function */
export type ProductMode = 'intraday' | 'cnc' | 'sips'

export interface CancelOrderModalProps {
  product?: Exclude<ProductMode, 'sips'>
  ind_stock_id: string | null
}
export interface TCancelOrderGET {
  status?: string
  data?: Datum[]
}

export interface Datum {
  stock_name?: string
  logo?: string
  vendor_order_id?: string
  txn_type?: string
  product?: string
  requested_qty?: number
  fulfilled_qty?: number
  requested_price?: string
  order_type?: string
  vyapar_status?: string
}

export default function CancelOrderModal({
  product,
  ind_stock_id,
  showModal,
  closeModal,
  refetch,
  getUrl,
  postUrl,
  placeOrderFn,
}: CancelOrderModalProps & {
  showModal: boolean
  closeModal: () => void
  refetch: () => void
  getUrl?: string
  postUrl?: string
  placeOrderFn?: () => void
}) {
  const router = useRouter()
  const [selectedStocks, setSelectedStocks] = useState<Array<string>>([])

  const { data, isLoading } = useQuery<TCancelOrderGET>(
    ['cancel-order-modal', product, ind_stock_id],
    () => {
      const token = Cookies.get('token')
      return fetchRequest({
        url:
          getUrl ??
          `${vyaparApiBaseUrl}/orders?status=open&response_format=rest&platform=web&product=${
            product ? product.toUpperCase() : ''
          }${ind_stock_id ? `&ind_stock_id=${ind_stock_id}` : ''}`,
        config: {
          headers: {
            Authorization: `Bearer ${token}`,
            Platform: 'web',
          },
        },
      })
    },
    {
      onSuccess(data) {
        const orderIdArr = data.data?.map(item => item.vendor_order_id ?? '')
        if (orderIdArr?.length) {
          setSelectedStocks(orderIdArr)
        }
      },
      onError() {
        void closeModal()
      },
    },
  )

  const {
    mutate,
    isLoading: mutationIsLoading,
    isSuccess: mutationIsSuccess,
  } = useMutation(['cancel-order-post', product, ind_stock_id], () => {
    const token = Cookies.get('token')
    return fetchPOSTRequest({
      method: 'POST',
      url:
        postUrl ??
        `${vyaparApiBaseUrl}/orders/cancel?response_format=rest${
          product ? `&product=${product.toUpperCase()}` : ''
        }`,
      headers: { Authorization: `Bearer ${token}`, Platform: 'web' },
      data: {
        selectedOrders: selectedStocks.map(id => {
          return {
            id,
          }
        }),
      },
    })
  })

  const handleSelect = (orderId: string) => {
    if (selectedStocks.includes(orderId)) {
      setSelectedStocks(prev => prev.filter(item => item !== orderId))
    } else {
      setSelectedStocks(prev => [...prev, orderId])
    }
  }
  if (isLoading || mutationIsLoading) {
    return (
      <Modal showModal={showModal} bodyClassName="max-w-lg">
        <SpinnerLoader />
      </Modal>
    )
  } else if (mutationIsSuccess) {
    return (
      <Modal showModal={showModal} bodyClassName="max-w-lg">
        <div className="flex flex-col items-center justify-center gap-3">
          <StencilCDNImage
            src="https://indcdn.indmoney.com/cdn/images/fe/green-tick-icon.png"
            width={70}
            height={70}
          />
          <p className="text-center text-lg font-medium">
            Order(s) Cancellation Requested
          </p>
          <p className="text-brand-grey-base text-center">
            {selectedStocks.length}{' '}
            {product === 'cnc' ? 'delivery' : 'intraday'} order(s) getting
            cancelled at the exchange
          </p>
          <div className="bg-brand-tertiary-orange text-brand-grey-dark w-full py-1 text-center text-xs">
            Blocked Balance will be released to your wallet
          </div>
          <button
            className="bg-brand-green-dark text-brand-white mt-2 w-3/4 self-center rounded-full py-2 "
            onClick={() => {
              void closeModal()
              void refetch()
              if (!getUrl) {
                void router.push(
                  `/investments/stocks/transactions?type=${product}`,
                )
              }
            }}
          >
            Continue
          </button>
        </div>
      </Modal>
    )
  } else {
    return (
      <Modal
        showModal={showModal}
        onClose={() => closeModal()}
        bodyClassName="max-w-lg"
      >
        <div className="flex flex-col justify-center p-2">
          <p className="text-brand-black text-lg font-medium">
            Cancel Open {product === 'cnc' ? 'Delivery' : 'Intraday'} Orders
          </p>
          <span
            className="text-brand-blue-azureRadiance mt-4 cursor-pointer text-sm"
            onClick={() => {
              const orderIdArr =
                data?.data?.map(item => item.vendor_order_id ?? '') ?? []
              if (selectedStocks.length === orderIdArr.length) {
                setSelectedStocks([])
              } else {
                setSelectedStocks(orderIdArr)
              }
            }}
          >
            Select All
          </span>
          <div className="max-h-72 overflow-scroll">
            {data?.data?.map(item => {
              return (
                <div
                  key={item.vendor_order_id}
                  className="my-2 flex cursor-pointer justify-between gap-2"
                  onClick={() => handleSelect(item.vendor_order_id ?? '')}
                >
                  <div className="w-1/10 self-center">
                    <Checkbox
                      inputClassName="focus:ring-0"
                      label=""
                      value={0}
                      name={`stock-check-${item.vendor_order_id}`}
                      checked={selectedStocks.includes(
                        item.vendor_order_id ?? '',
                      )}
                    />
                  </div>
                  <div className=" w-9/10 shadow-one flex justify-between rounded-lg px-2 py-4">
                    <div className="flex items-center gap-2 ">
                      {item.logo && (
                        <div className="rounded-full">
                          <StencilCDNImage
                            src={item.logo}
                            width={40}
                            height={40}
                          />
                        </div>
                      )}
                      <div className="text-sm">
                        <p>{item.stock_name}</p>

                        <p className="text-xs">{item.requested_qty} Qty</p>
                      </div>
                    </div>
                    <div className=" text-right">
                      <p className="text-xs">
                        {item.txn_type?.toLowerCase() === 'buy'
                          ? 'Buy'
                          : 'Sell'}{' '}
                        at{' '}
                        {item.order_type?.toLowerCase() === 'limit'
                          ? 'limit price'
                          : item.order_type?.toLowerCase() === 'market'
                          ? 'market price'
                          : item.order_type?.toLowerCase().includes('stop')
                          ? 'stop loss'
                          : ''}
                      </p>
                      <p className="text-sm">
                        {formatNumberToINRCommaSeparatedString(
                          Number(item.requested_price),
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <button
            className="bg-brand-green-dark text-brand-white mt-2 w-3/4 self-center rounded-full py-2 transition-opacity disabled:opacity-60"
            disabled={!selectedStocks.length}
            onClick={() => mutate()}
          >
            Cancel Orders & Proceed
          </button>

          {getUrl && placeOrderFn && (
            <button
              className=" text-brand-grey-dark mt-2 w-3/4 self-center rounded-full py-2 transition-opacity disabled:opacity-60"
              onClick={() => placeOrderFn()}
            >
              Do not cancel & Proceed
            </button>
          )}
        </div>
      </Modal>
    )
  }
}
